import { createContext, useContext, useState } from 'react';
import { EThemeNames } from '../../../domain/types/themePresets';

type ThemeContextValue = {
  themeName: EThemeNames;
  setThemeName: (themeName: EThemeNames) => void;
};

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<
  React.PropsWithChildren<{ defaultTheme: EThemeNames }>
> = ({ defaultTheme = EThemeNames.LEEDFLOW_LIGHT, children }) => {
  const [themeName, setThemeName] = useState<EThemeNames>(() => {
    // Initialize the theme from the localstorage if possible,
    // otherwise fall back to the default theme.
    const localTheme = localStorage.getItem('lf-theme');
    if (localTheme && Object.values(EThemeNames).includes(localTheme as EThemeNames)) {
      return localTheme as EThemeNames;
    }

    return defaultTheme;
  });

  const handleThemeChange = (newTheme: EThemeNames) => {
    if (newTheme === themeName || !Object.values(EThemeNames).includes(newTheme)) return;

    // Update the theme
    setThemeName(newTheme);

    // Update the localstorage
    localStorage.setItem('lf-theme', newTheme);
  };

  return (
    <ThemeContext.Provider value={{ themeName, setThemeName: handleThemeChange }}>
      {children}
    </ThemeContext.Provider>
  );
};
